import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { dataUriToBlob } from '@softline/core';
import {
  ButtonComponent,
  IconComponent,
  ResolvableModal,
  SignaturePadComponent,
  SoftSlotDirective,
} from '@softline/ui-core';

@Component({
  selector: 'soft-garage-signature-modal',
  templateUrl: './signature-modal.component.html',
  styleUrl: './signature-modal.component.css',
  imports: [
    DatePipe,
    ButtonComponent,
    IconComponent,
    SignaturePadComponent,
    SoftSlotDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GarageSignatureModalComponent extends ResolvableModal<Blob | null> {
  private readonly destroyRef = inject(DestroyRef);

  private readonly pad = viewChild.required(SignaturePadComponent);
  private readonly paper = viewChild.required<ElementRef<HTMLElement>>('paper');

  protected readonly isEmpty = signal(true);
  protected readonly today = new Date();

  protected readonly padOptions = {
    minWidth: 1.25,
    maxWidth: 3,
    penColor: '#1e3a8a',
    throttle: 8,
  };

  constructor() {
    super();
    afterNextRender(() => {
      this.resizeCanvas();

      // The wrapper's onBeginEvent/onEndEvent outputs are wired to the old
      // v2 callback API the vendored v4 pad no longer calls — listen to the
      // pad's beginStroke/endStroke CustomEvents directly instead.
      const nativePad = this.pad().queryPad();
      const onBegin = () => this.onStrokeBegin();
      const onEnd = () => this.onStrokeEnd();
      nativePad?.addEventListener('beginStroke', onBegin);
      nativePad?.addEventListener('endStroke', onEnd);

      const observer = new ResizeObserver(() => this.resizeCanvas());
      observer.observe(this.paper().nativeElement);

      this.destroyRef.onDestroy(() => {
        nativePad?.removeEventListener('beginStroke', onBegin);
        nativePad?.removeEventListener('endStroke', onEnd);
        observer.disconnect();
      });
    });
  }

  protected onStrokeBegin(): void {
    this.isEmpty.set(false);
  }

  protected onStrokeEnd(): void {
    this.isEmpty.set(this.pad().isEmpty());
  }

  protected undo(): void {
    const pad = this.pad();
    const strokes = pad.toData();
    if (strokes.length === 0) return;
    strokes.pop();
    pad.clear();
    if (strokes.length > 0) pad.fromData(strokes);
    this.isEmpty.set(pad.isEmpty());
  }

  protected clear(): void {
    this.pad().clear();
    this.isEmpty.set(true);
  }

  protected cancel(): void {
    this.resolve(null);
  }

  protected save(): void {
    const pad = this.pad();
    if (pad.isEmpty()) return;
    this.resolve(dataUriToBlob(pad.toDataURL('image/png')));
  }

  // The pad draws in CSS pixels; scale the canvas backing store by the device
  // pixel ratio so signatures stay sharp, and redraw existing strokes so a
  // resize (rotation, window drag) never loses what was already signed.
  private resizeCanvas(): void {
    const pad = this.pad();
    const paper = this.paper().nativeElement;
    const canvas = paper.querySelector('canvas');
    const width = paper.clientWidth;
    const height = paper.clientHeight;
    if (!canvas || width === 0 || height === 0) return;

    const strokes = pad.toData();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.getContext('2d')?.scale(ratio, ratio);
    pad.clear();
    if (strokes.length > 0) pad.fromData(strokes);
    this.isEmpty.set(pad.isEmpty());
  }
}
